import { CouplingEnum, StrainIndexRiskEnum } from './enum';

type Risk = {
	title: string;
	color: string;
};

type Option = {
	label: string;
	value: number;
};

type Metric = {
	title: string;
	description: string;
};

type Mapper = {
	[key: string]: string;
};

type RisksMapper = Record<string, Risk>;

type LabelsMapper = Record<string, string>;

type MetricsMapper = Record<string, Metric>;

type DurationMapper = Record<string, Option>;

type DiagnosticsMapper = Record<string, Mapper>;

export const REPORT_COMMENT_DEBOUNCE_TIME = 3000;
export const URL_LOGO = 'https://kinebot-statics.s3.amazonaws.com/kinebot_logo.png';
export const DIAGNOSTIC_COLORS = ['#ABABAB', '#40BF5F', '#FDC365', '#F07575', '#8E85EF'];
export const BACKGROUND_IMPORT_THRESHOLD = 150;

export const STATUS_COLOR_SUCCESS = '#17A93B';
export const STATUS_COLOR_PRIMARY = '#002FBB';
export const STATUS_COLOR_ERROR = '#D13342';
export const STATUS_COLOR_WARNING = '#FF8C00';
export const STATUS_COLOR_WARNING_SECONDARY = '#AD4E00';
export const STATUS_COLOR_DISABLED = '#8C8C8C';

export const WARNING_BACKGROUND_COLOR = '#FFF7E6';
export const WARNING_BORDER_COLOR = '#FFE58F';

export const NONE_RISK_COLOR = '#E0E0E0';
export const ACCEPTABLE_RISK_COLOR = '#2CC852';
export const MODERATE_RISK_COLOR = '#F8D627';
export const HIGH_RISK_COLOR = '#F78A38';
export const VERY_HIGH_RISK_COLOR = '#E74150';
export const SERIOUS_RISK_COLOR = '#9B54E2';

export const NONE_RISK_RESULT = 0;
export const ACCEPTABLE_RISK_RESULT = 1;
export const MODERATE_RISK_RESULT = 2;
export const HIGH_RISK_RESULT = 3;
export const VERY_HIGH_RISK_RESULT = 4;
export const SERIOUS_RISK_RESULT = 5;

export const EWA_CUSTOM_REPORT = 'ewa_kinebot';

export const REBA_GRADE_LABELS = {
	NOT_IDENTIFIED: 'Not identified',
	SAFE: 'Safe',
	INFO: 'Info',
	ATTENTION: 'Attention',
	WARNING: 'Warning',
	DANGER: 'Danger'
};

export const REBA_MIN_ACCEPTABLE_RISK_SCORE = 1;
export const REBA_MIN_MODERATE_RISK_SCORE = 2;
export const REBA_MIN_HIGH_RISK_SCORE = 4;
export const REBA_MIN_VERY_HIGH_RISK_SCORE = 8;
export const REBA_MIN_SERIOUS_RISK_SCORE = 11;

export const RULA_MIN_ACCEPTABLE_RISK_SCORE = 1;
export const RULA_MIN_MODERATE_RISK_SCORE = 3;
export const RULA_MIN_VERY_HIGH_RISK_SCORE = 5;
export const RULA_SERIOUS_RISK_SCORE = 7;

export const ANSI_THREE_LEVEL_MIN_MODERATE_RISK = 2;
export const ANSI_THREE_LEVEL_MIN_VERY_HIGH_RISK = 4;
export const ANSI_TWO_LEVEL_MIN_VERY_HIGH_RISK = 6;

export const REBA_GRADES = [
	{ title: REBA_GRADE_LABELS.NOT_IDENTIFIED, color: NONE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.SAFE, color: ACCEPTABLE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.INFO, color: MODERATE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.ATTENTION, color: HIGH_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.WARNING, color: VERY_HIGH_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.DANGER, color: SERIOUS_RISK_COLOR }
];

export const REBA_BODY_PART_GRADES = [
	{ title: REBA_GRADE_LABELS.NOT_IDENTIFIED, color: NONE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.SAFE, color: ACCEPTABLE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.INFO, color: MODERATE_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.WARNING, color: VERY_HIGH_RISK_COLOR },
	{ title: REBA_GRADE_LABELS.DANGER, color: SERIOUS_RISK_COLOR }
];

export const RULA_RISKS = [
	{ name: 'Not identified', color: NONE_RISK_COLOR },
	{ name: 'Acceptable risk', color: ACCEPTABLE_RISK_COLOR },
	{ name: 'Moderate risk', color: MODERATE_RISK_COLOR },
	{ name: 'Investigate', color: VERY_HIGH_RISK_COLOR },
	{ name: 'Investigate and change now', color: SERIOUS_RISK_COLOR }
];

export const NIOSH_RISKS: RisksMapper = {
	VERY_LOW: { title: 'Very low', color: ACCEPTABLE_RISK_COLOR },
	LOW: { title: 'Low', color: MODERATE_RISK_COLOR },
	MODERATE: { title: 'Moderate', color: HIGH_RISK_COLOR },
	HIGH: { title: 'High', color: VERY_HIGH_RISK_COLOR },
	VERY_HIGH: { title: 'Very high', color: SERIOUS_RISK_COLOR }
};

export const NIOSH_DURATION_METER: DurationMapper = {
	LESS_THAN_1H: { label: '1 hour', value: 1 },
	BETWEEN_1_AND_2H: { label: '1 - 2 hours', value: 2 },
	BETWEEN_2_AND_8H: { label: '2 - 8 hours', value: 8 }
};

export const COUPLING_LABEL: LabelsMapper = {
	[CouplingEnum.POOR]: 'Poor',
	[CouplingEnum.FAIR]: 'Fair',
	[CouplingEnum.GOOD]: 'Good'
};

export const KIM_PP_RISKS: RisksMapper = {
	LOW: { title: 'Low', color: ACCEPTABLE_RISK_COLOR },
	MODERATE: { title: 'Moderate', color: MODERATE_RISK_COLOR },
	HIGH: { title: 'High', color: HIGH_RISK_COLOR },
	VERY_HIGH: { title: 'Very high', color: VERY_HIGH_RISK_COLOR }
};

export const KIM_MHO_RISKS: RisksMapper = {
	LOW: { title: 'Low', color: ACCEPTABLE_RISK_COLOR },
	SLIGHTLY_INCREASED: { title: 'Slightly increased', color: MODERATE_RISK_COLOR },
	SUBSTANTIALLY_INCREASED: { title: 'Substantially increased', color: HIGH_RISK_COLOR },
	HIGH: { title: 'High', color: VERY_HIGH_RISK_COLOR }
};

export const KIM_MHO_DIAGNOSTICS: DiagnosticsMapper = {
	LOW: {
		PHYSICAL_OVERLOAD: 'Physical overload is unlikely.',
		HEALTH_CONSEQUENCES: 'No health risk is to be expected.',
		MEASURES: 'None.'
	},
	SLIGHTLY_INCREASED: {
		PHYSICAL_OVERLOAD: 'Physical overload is possible for less resilient persons.',
		HEALTH_CONSEQUENCES: 'Fatigue, low-grade adaptation problems which can be compensated for during leisure time.',
		MEASURES: 'For less resilient persons, workplace redesign and other prevention measures may be helpful.'
	},
	SUBSTANTIALLY_INCREASED: {
		PHYSICAL_OVERLOAD: 'Physical overload is possible for normally resilient persons.',
		HEALTH_CONSEQUENCES:
			'Disorders (pain), possibly including dysfunctions, reversible in most cases, without morphological manifestation.',
		MEASURES: 'Workplace redesign and other prevention measures should be considered.'
	},
	HIGH: {
		PHYSICAL_OVERLOAD: 'Physical overload is likely.',
		HEALTH_CONSEQUENCES:
			'More pronounced disorders and/or dysfunctions, structural damage with pathological significance.',
		MEASURES: 'Workplace redesign measures are necessary. Other prevention measures should be considered.'
	}
};

export const KIM_MHO_FORCE_TRANSFERS: MetricsMapper = {
	OPTIMUM: {
		title: 'Optimum force application',
		description:
			'Working objects are easy to grip (e.g. bar-shaped, gripping grooves) / good ergonomic gripping design (grips, buttons, tools).'
	},
	RESTRICTED: {
		title: 'Restricted force application',
		description: 'Greater holding forces required / no shaped grips.'
	},
	HINDERED: {
		title: 'Force application considerably hindered',
		description:
			'Working objects hardly possible to grip (slippery, soft, sharp edges) / no or only unsuitable grips.'
	}
};

export const KIM_MHO_FORCE_INTENSITIES: MetricsMapper = {
	VERY_LOW: {
		title: 'Very low',
		description:
			'Up to 15% Fmax. Button actuation, shifting, ordering, material guidance, insertion of small parts.'
	},
	MODERATE: {
		title: 'Moderate',
		description: 'Up to 30% Fmax. Gripping, joining small work pieces by hand or with small tools.'
	},
	HIGH: {
		title: 'High',
		description:
			'Up to 50% Fmax. Turning, winding, packaging, grasping, holding or joining parts, pressing in, cutting, working with small powered hand tools.'
	},
	VERY_HIGH: {
		title: 'Very high',
		description:
			'Up to 80% Fmax. Cutting involving major element of force, working with small staple guns, moving or holding parts or tools.'
	},
	PEAK: {
		title: 'Peak forces',
		description: 'More than 80% Fmax. Tightening, loosening bolts, separating, pressing in.'
	},
	POWERFUL_HITTING: {
		title: 'Powerful hitting',
		description: 'Hitting with the ball of the thumb, palm of the hand or fist.'
	}
};

export const STRAIN_INDEX_RISKS: RisksMapper = {
	[StrainIndexRiskEnum.SAFE]: { title: 'Safe', color: ACCEPTABLE_RISK_COLOR },
	[StrainIndexRiskEnum.HAZARDOUS]: { title: 'Hazardous', color: VERY_HIGH_RISK_COLOR }
};

export const OWAS_RISK_COLORS: Mapper = {
	1: ACCEPTABLE_RISK_COLOR,
	2: MODERATE_RISK_COLOR,
	3: VERY_HIGH_RISK_COLOR,
	4: SERIOUS_RISK_COLOR
};

export const PSYCHOSOCIAL_TOOLS_MAPPER: Mapper = {
	'NASA-TLX': 'nasa_tlx',
	'HSE-IT': 'hse_it'
};

export const PSYCHOSOCIAL_CATEGORIES_MAPPER: Mapper = {
	demands: 'Demands',
	relationship: 'Relationship',
	duty: 'Duty',
	change: 'Change ',
	control: 'Control',
	management_support: 'Management Support',
	colleagues_support: 'Colleagues Support',
	performance: 'Achievement level',
	temporal: 'Temporal Demands',
	frustration: 'Frustration level',
	effort: 'Effort level',
	physical: 'Physical Demands',
	mental: 'Mental Demands'
};
